/**
 * Support at Home suggestion engine — entity funding gate (Phase 2).
 * Mirrors aged care `suggestionEngineFunding.js` liquid pool rules for SAH strategies.
 */
import { getInvestmentPropertyTaxSummary } from '../../../shared/src/calculation/entityDisposalTax.ts';
import { getEntityIdFromOwnership } from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { isAssetEligibleForSuggestionRadFunding } from './entityFundingUtils.ts';
import { applyInvestmentPropertySaleToFactFind } from './entityInvestmentPropertySale.ts';
import type { InvestmentPropertySaleDetails } from './entityInvestmentPropertySale.ts';
import { getEntityInvestmentPropertiesForSuggestions } from './suggestionEngineFunding.ts';
import { SAH_EMERGENCY_FUND_RESERVE } from '../strategy/supportAtHomeStrategyFunding.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

export { isAssetEligibleForSuggestionRadFunding, isStructureFundingIncludedInSuggestions } from './entityFundingUtils.ts';

export { getEntityInvestmentPropertiesForSuggestions };

/** One liquid asset available to the SAH suggestion engine, ranked by priority (lower = used first). */
export interface SahAvailableAsset {
    type: string;
    id: string | number | undefined;
    description: string;
    value: number;
    available: number;
    priority: number;
}

/**
 * Liquid assets the SAH suggestion engine may use for gifting, funeral bonds, debt repayment, etc.
 */
export function collectLiquidAssetsForSahSuggestions(
    factFind: DraftFactFind,
    emergencyFundReserve: number = SAH_EMERGENCY_FUND_RESERVE,
): SahAvailableAsset[] {
    const assets: SahAvailableAsset[] = [];

    (factFind.assets?.bankAccounts || []).forEach((account) => {
        if (!isAssetEligibleForSuggestionRadFunding(factFind, account)) return;
        const value = account.value || 0;
        if (value > 0) {
            assets.push({
                type: 'bankAccounts',
                id: account.id,
                description: account.description || 'Bank Account',
                value,
                available: Math.max(0, value - emergencyFundReserve),
                priority: 1,
            });
        }
    });

    (factFind.assets?.accountBasedPensions || []).forEach((abp) => {
        if (!isAssetEligibleForSuggestionRadFunding(factFind, abp)) return;
        const value = abp.value || 0;
        if (value > 0) {
            assets.push({
                type: 'accountBasedPensions',
                id: abp.id,
                description: abp.description || 'Account-Based Pension',
                value,
                available: value * 0.5,
                priority: 2,
            });
        }
    });

    (factFind.assets?.shares || []).forEach((share) => {
        if (!isAssetEligibleForSuggestionRadFunding(factFind, share)) return;
        const value = share.value || 0;
        if (value > 0) {
            assets.push({
                type: 'shares',
                id: share.id,
                description: share.description || 'Shares',
                value,
                available: value,
                priority: 3,
            });
        }
    });

    (factFind.assets?.termDeposits || []).forEach((td) => {
        if (!isAssetEligibleForSuggestionRadFunding(factFind, td)) return;
        const value = td.value || 0;
        if (value > 0) {
            assets.push({
                type: 'termDeposits',
                id: td.id,
                description: td.description || 'Term deposit',
                value,
                available: value,
                priority: 2,
            });
        }
    });

    return assets.sort((a, b) => a.priority - b.priority);
}

export function buildSahEntityIpSaleDetails(
    factFind: DraftFactFind,
    property: { id?: string | number; value?: number; costBase?: number },
): InvestmentPropertySaleDetails {
    const salePrice = property.value || 0;
    return {
        selectedPropertyId: property.id,
        salePrice,
        sellingCosts: salePrice * 0.025,
        costBase: property.costBase,
        radAllocation: 0,
        newHomePurchasePrice: 0,
        downsizerContribution: 0,
        partnerDownsizerContribution: 0,
        giftingAmount: 0,
        funeralBondPurchase: 0,
    };
}

export interface EntityIpSaleStrategy {
    type: 'sellEntityInvestmentProperty';
    investmentPropertyDetails: InvestmentPropertySaleDetails;
    structureFundingIncluded: true;
    groupKey: string;
    name: string;
    description: string;
}

/**
 * Whole entity-owned IP sale scenarios (include gate only). Illiquid — never fractional.
 * @returns Strategy configs for `SupportAtHomeStrategyGenerator`
 */
export function generateEntityIpSaleStrategies(factFind: DraftFactFind): EntityIpSaleStrategy[] {
    const strategies: EntityIpSaleStrategy[] = [];
    const entityIps = getEntityInvestmentPropertiesForSuggestions(factFind);
    if (!entityIps.length) return strategies;

    entityIps.forEach((property) => {
        const entityId = getEntityIdFromOwnership(property.ownership);
        const structure = (factFind.structures || []).find((s) => s.id === entityId);
        const propLabel =
            property.description?.trim() ||
            structure?.name?.trim() ||
            'entity investment property';

        const details = buildSahEntityIpSaleDetails(factFind, property);
        // DraftFactFind is structurally compatible with FactFind (see
        // types/formDraft.ts); the shared calc-engine fn defensively parseFloat/coerces internally
        // regardless, so this cast is safe (see factFindTypes.ts header).
        const summary = getInvestmentPropertyTaxSummary(factFind as unknown as FactFind, details);
        const netProceeds = summary?.netProceeds ?? 0;
        if (netProceeds <= 0) return;

        strategies.push({
            type: 'sellEntityInvestmentProperty',
            investmentPropertyDetails: details,
            structureFundingIncluded: true,
            groupKey: `entity-ip-sale-${property.id}`,
            name: `Sell ${propLabel} (entity pool)`,
            description: `Sell entity-owned investment property; estimated net $${Math.round(netProceeds).toLocaleString()} to entity liquid pool after CGT`,
        });
    });

    return strategies;
}

/** Apply whole entity IP sale for SAH suggestion engine evaluation. */
export function applySahEntityIpSaleToFactFind(
    factFind: DraftFactFind,
    baselineFactFind: DraftFactFind,
    details: InvestmentPropertySaleDetails,
): DraftFactFind {
    return applyInvestmentPropertySaleToFactFind(factFind, baselineFactFind, details);
}
